/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { Box, Button, Divider, List, ListItem, Stack, SxProps, Theme, Typography } from "@mui/material";
import IconCivilLaw from "@/src/assets/iconsRedesign2026/civilLaw/toggleable/IconCivilLaw";
import { AddToPhotosOutlined, ChevronRight, LogoutOutlined } from "@mui/icons-material";
import { mergeSx } from "@/src/utils/mergeSx";
import { authenticatedNavigationStyle } from "@/src/components/Navigation/AuthenticatedNavigation/authenticatedNavigationStyle";
import { Link, useNavigate } from "react-router";
import CustomerAvatar from "@/src/components/CustomerAvatar/CustomerAvatar";
import { useCustomer } from "@/src/provider/Customer/CustomerProvider";
import { logout } from "@/src/store/login/actions";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import AuthenticatedNavigationLinkCase from "@/src/components/Navigation/AuthenticatedNavigation/AuthenticatedNavigationLinkCase";
import { useBasicAccountData } from "@/src/provider/BasicAccountData/BasicAccountDataProvider";
import { useFlexServiceLayoutForCustomer } from "@/src/services/advocardService";
import { useQueryClient } from "@tanstack/react-query";
import AuthenticatedNavigationLinkDefault
  from "@/src/components/Navigation/AuthenticatedNavigation/AuthenticatedNavigationLinkDefault";
import AuthenticatedNavigationLinkMessages
  from "@/src/components/Navigation/AuthenticatedNavigation/AuthenticatedNavigationLinkMessages";

export type AuthenticatedNavigationProps = {
  sx?: SxProps<Theme>;
  onNeedsAttention?: (needsAttention: boolean) => void;
  onClose?: Function
};

export default function AuthenticatedNavigation({ sx, onClose }: AuthenticatedNavigationProps) {
  const navigate = useNavigate();
  const { customer } = useCustomer();
  const queryClient = useQueryClient();

  const { activeMinimalCases, isLoading: isLoadingBasicAccountData } = useBasicAccountData();

  const handleLogout = () => {
    logout(queryClient);
    navigate("/");
  };

  if (!customer || isLoadingBasicAccountData) {
    return (
      <Stack sx={{ height: "100%", justifyContent: "center" }}>
        <LegalbirdLoader />
      </Stack>
    );
  }

  return (
    <Stack sx={mergeSx(authenticatedNavigationStyle.authenticatedNavigation, sx)}>
      <Box component={"nav"} sx={{ flexShrink: 0 }}>
        <List disablePadding>
          <AuthenticatedNavigationLinkDefault type={"customIcon"} icon={IconCivilLaw} label={"Meine Fälle"} to={"/servicewelt"} />
          <Divider />
          {!useFlexServiceLayoutForCustomer(customer) && (
            <>
              <AuthenticatedNavigationLinkDefault type={"muiIcon"} icon={AddToPhotosOutlined} label={"Fall anlegen"} to={"/servicewelt/neuer-fall"} />
              <Divider />
            </>
          )}
          <AuthenticatedNavigationLinkMessages />
          {!!activeMinimalCases?.length && <Divider />}
        </List>
      </Box>
      <Stack sx={authenticatedNavigationStyle.caseList}>
        {!!activeMinimalCases?.length ? (
          <>
            <Typography sx={{ margin: "0 0 0.5rem 0.5rem" }}>Aktive Fälle</Typography>
            <List sx={{ overflowY: "auto" }}>
              {activeMinimalCases?.map((activeCase) => (
                <ListItem key={activeCase.id} disablePadding>
                  <AuthenticatedNavigationLinkCase product={activeCase} onClose={onClose} />
                </ListItem>
              ))}
            </List>
          </>
        ) : (
          <Typography sx={(theme) => ({ color: theme.palette.text.disabled })}>Noch keine Fälle vorhanden</Typography>
        )}
      </Stack>
      <Stack spacing={1} sx={{ display: { xs: "block", lg: "none" }, marginTop: "auto", flexShrink: 0 }}>
        <Divider />
        <Stack component={Link} direction={"row"} sx={{ padding: "0.75rem 0.5rem", alignItems: "center" }} spacing={1.5} to={"/servicewelt/mein-konto"}>
          <CustomerAvatar />
          <Box>
            <Typography
              component={"div"}
              variant={"button"}
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                color: theme.palette.primary.main,
              })}
            >
              {customer.person.fullName} <ChevronRight fontSize={"small"} />
            </Typography>
            <Typography component={"div"} variant={"caption"} sx={(theme) => ({ color: theme.palette.text.primary })}>
              Mein Konto
            </Typography>
          </Box>
        </Stack>
        <Divider />
        <Button variant={"text"} startIcon={<LogoutOutlined />} sx={{ width: "100%", justifyContent: "flex-start", padding: "1rem" }} onClick={handleLogout}>
          Abmelden
        </Button>
      </Stack>
    </Stack>
  );
}
